import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Download, FileArchive, FileText, Upload, Loader2, CheckCircle2,
  SkipForward, XCircle, RotateCcw, FileSpreadsheet
} from "lucide-react";

const ACTION_BADGE = {
  create: { cls: "bg-emerald-100 text-emerald-700", label: "Create", Icon: CheckCircle2 },
  skip: { cls: "bg-amber-100 text-amber-700", label: "Skip", Icon: SkipForward },
  error: { cls: "bg-red-100 text-red-700", label: "Error", Icon: XCircle },
};

async function downloadBlob(url, filename) {
  try {
    const res = await api.get(url, { responseType: "blob" });
    const blobUrl = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = blobUrl; a.download = filename; a.click();
    setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
  } catch {
    toast.error("Download failed — please try again");
  }
}

export default function DataImport({ companyId, companyName }) {
  const [specs, setSpecs] = useState([]);
  const [entity, setEntity] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(null); // "preview" | "commit"
  const fileRef = useRef(null);

  useEffect(() => { api.get("/admin/import/specs").then((r) => setSpecs(r.data)); }, []);

  const reset = () => { setFile(null); setPreview(null); setReport(null); if (fileRef.current) fileRef.current.value = ""; };

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f); setPreview(null); setReport(null);
  };

  const runPreview = async () => {
    if (!entity) return toast.error("Choose the data type first");
    if (!file) return toast.error("Choose a CSV or Excel file");
    setBusy("preview");
    try {
      const fd = new FormData();
      fd.append("company_id", companyId); fd.append("entity", entity); fd.append("file", file);
      const { data } = await api.post("/admin/import/preview", fd);
      setPreview(data); setReport(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not read the file"); }
    finally { setBusy(null); }
  };

  const runCommit = async () => {
    setBusy("commit");
    try {
      const fd = new FormData();
      fd.append("company_id", companyId); fd.append("entity", entity); fd.append("file", file);
      const { data } = await api.post("/admin/import/commit", fd);
      setReport(data); setPreview(null);
      toast.success(`${data.counts.create} record(s) imported into ${companyName}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    finally { setBusy(null); }
  };

  const spec = specs.find((s) => s.entity === entity);

  return (
    <div className="space-y-5 max-w-4xl" data-testid="data-import-panel">
      {/* Templates */}
      <div className="bg-white border border-slate-200 rounded-md p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-head font-bold text-slate-900">1 · Give the customer the templates</h3>
            <p className="text-sm text-slate-500 mt-0.5 max-w-lg">Each CSV has the exact column headings plus two example rows. The customer fills them in (Excel is fine) and sends them back. Import in the numbered order so records link together.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" data-testid="import-download-instructions" onClick={() => downloadBlob("/admin/import/instructions", "amden-import-instructions.txt")}>
              <FileText className="h-4 w-4 mr-1" />Instructions
            </Button>
            <Button className="btn-accent" size="sm" data-testid="import-download-all" onClick={() => downloadBlob("/admin/import/templates.zip", "amden-data-import-pack.zip")}>
              <FileArchive className="h-4 w-4 mr-1" />Download all (ZIP)
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4">
          {specs.map((s) => (
            <button key={s.entity} data-testid={`import-template-${s.entity}`} title={`Download the ${s.label} template`}
              onClick={() => downloadBlob(`/admin/import/template/${s.entity}`, `amden-import-${s.entity}.csv`)}
              className="flex items-center gap-2 border border-slate-200 rounded-md px-3 py-2.5 text-left hover:border-slate-400 hover:bg-slate-50 transition-colors">
              <FileSpreadsheet className="h-4 w-4 text-emerald-600 flex-shrink-0" />
              <span className="min-w-0">
                <span className="block text-sm font-medium text-slate-800 truncate">{s.order}. {s.label}</span>
                <span className="block text-[11px] text-slate-400">{s.columns.length} columns</span>
              </span>
              <Download className="h-3.5 w-3.5 text-slate-500 ml-auto flex-shrink-0" />
            </button>
          ))}
        </div>
      </div>

      {/* Upload & import */}
      <div className="bg-white border border-slate-200 rounded-md p-5">
        <h3 className="font-head font-bold text-slate-900">2 · Upload the filled file into {companyName}</h3>
        <p className="text-sm text-slate-500 mt-0.5">CSV or Excel (.xlsx). You'll see a validation preview before anything is saved — duplicates are skipped, never overwritten.</p>
        <div className="flex items-end gap-3 flex-wrap mt-4">
          <div className="w-56">
            <label className="text-xs text-slate-600 block mb-1">Data type</label>
            <Select value={entity} onValueChange={(v) => { setEntity(v); setPreview(null); setReport(null); }}>
              <SelectTrigger data-testid="import-entity-select" className="bg-white"><SelectValue placeholder="Choose data type…" /></SelectTrigger>
              <SelectContent className="bg-white">{specs.map((s) => <SelectItem key={s.entity} value={s.entity}>{s.order}. {s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-600 block mb-1">File (.csv / .xlsx)</label>
            <input ref={fileRef} data-testid="import-file-input" type="file" accept=".csv,.xlsx,.txt" onChange={pickFile}
              className="block text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:font-medium file:text-slate-700 hover:file:bg-slate-200 cursor-pointer" />
          </div>
          <Button data-testid="import-preview-btn" variant="outline" onClick={runPreview} disabled={busy !== null || !file || !entity}>
            {busy === "preview" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}Validate & preview
          </Button>
          {(preview || report) && <Button variant="ghost" size="sm" data-testid="import-reset" onClick={reset}><RotateCcw className="h-3.5 w-3.5 mr-1" />Start over</Button>}
        </div>

        {spec && !preview && !report && (
          <p className="text-[11px] text-slate-400 mt-3">
            Required columns: {spec.columns.filter((c) => c.required).map((c) => c.header).join(", ")}
            {spec.links.length > 0 && <> · Links by name: {spec.links.map((l) => l.replace("_", " ")).join(", ")}</>}
          </p>
        )}

        {/* Preview */}
        {preview && (
          <div className="mt-5" data-testid="import-preview">
            <div className="flex items-center gap-2 flex-wrap mb-3">
              <span className="text-sm font-medium text-slate-700">{preview.filename} · {preview.total} row(s)</span>
              <span className="text-xs font-semibold bg-emerald-100 text-emerald-700 rounded px-2 py-0.5" data-testid="preview-count-create">{preview.counts.create} to create</span>
              <span className="text-xs font-semibold bg-amber-100 text-amber-700 rounded px-2 py-0.5" data-testid="preview-count-skip">{preview.counts.skip} skipped</span>
              <span className="text-xs font-semibold bg-red-100 text-red-700 rounded px-2 py-0.5" data-testid="preview-count-error">{preview.counts.error} error(s)</span>
            </div>
            {preview.ignored_columns.length > 0 && (
              <p className="text-xs text-amber-600 mb-2">Ignored unknown column(s): {preview.ignored_columns.join(", ")}</p>
            )}
            <div className="border border-slate-200 rounded-md overflow-hidden max-h-80 overflow-y-auto scroll-thin">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0"><tr>{["Row", "Record", "Action", "Details"].map((h) => <th key={h} className="text-left font-medium text-slate-500 px-3 py-2 text-xs uppercase tracking-wide">{h}</th>)}</tr></thead>
                <tbody>
                  {preview.rows.map((r) => {
                    const b = ACTION_BADGE[r.action];
                    return (
                      <tr key={r.row} className="border-t border-slate-100" data-testid={`preview-row-${r.row}`}>
                        <td className="px-3 py-2 text-slate-400 text-xs">{r.row}</td>
                        <td className="px-3 py-2 font-medium text-slate-800">{r.display}</td>
                        <td className="px-3 py-2"><span className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded px-1.5 py-0.5 ${b.cls}`}><b.Icon className="h-3 w-3" />{b.label}</span></td>
                        <td className="px-3 py-2 text-xs text-slate-500">{(r.reasons || []).join(" · ") || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Button className="btn-accent mt-3" data-testid="import-commit-btn" onClick={runCommit} disabled={busy !== null || preview.counts.create === 0}>
              {busy === "commit" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
              Import {preview.counts.create} record(s) into {companyName}
            </Button>
          </div>
        )}

        {/* Report */}
        {report && (
          <div className="mt-5 space-y-3" data-testid="import-report">
            <div className="flex items-center gap-2 rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2.5 text-sm text-emerald-800">
              <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
              <b>{report.counts.create} created</b> · {report.counts.skip} skipped · {report.counts.error} error(s) — logged in the audit trail.
            </div>
            {report.created.length > 0 && (
              <div className="text-xs text-slate-600">
                <b className="text-slate-700">Created:</b> {report.created.map((c) => `${c.ref} ${c.display}`).join(" · ")}
              </div>
            )}
            {report.skipped.length > 0 && (
              <div className="text-xs text-amber-700">
                <b>Skipped:</b> {report.skipped.map((s) => `Row ${s.row} (${s.display}) — ${s.reasons[0]}`).join(" · ")}
              </div>
            )}
            {report.errors.length > 0 && (
              <div className="text-xs text-red-700">
                <b>Errors (fix in the file and re-upload):</b> {report.errors.map((e) => `Row ${e.row} — ${e.reasons.join(", ")}`).join(" · ")}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
